/**
 * Volume Role - Format Pillar
 *
 * Formata valores de volume para apresentação.
 *
 * @example
 * import { formatVolume } from '@attrx/role-morphic/volume/format';
 *
 * formatVolume('liter', 2.5);                      // "2.5 L"
 * formatVolume('liter', 2.5, { verbose: true });   // "2.5 liters"
 * formatVolume('milliliter', 1000, { locale: 'pt-BR' }); // "1.000 mL"
 */

import { VOLUME_UNITS, type VolumeUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface VolumeFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de volume para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatVolume('liter', 2.5);                        // "2.5 L"
 * formatVolume('liter', 2.5, { verbose: true });     // "2.5 liters"
 * formatVolume('liter', 1, { verbose: true });       // "1 liter"
 * formatVolume('milliliter', 1000, { locale: 'pt-BR' }); // "1.000 mL"
 * formatVolume('gallon_us', 5, { decimals: 0 });     // "5 gal"
 */
export function formatVolume(
  variant: VolumeUnit,
  value: number,
  options: VolumeFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = VOLUME_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown volume variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getVolumeSymbol('liter');       // "L"
 * getVolumeSymbol('gallon_us');   // "gal"
 * getVolumeSymbol('milliliter');  // "mL"
 */
export function getVolumeSymbol(variant: VolumeUnit): string {
  const unit = VOLUME_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown volume variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getVolumeName('liter', 1);       // "liter"
 * getVolumeName('liter', 2);       // "liters"
 * getVolumeName('gallon_us', 1);   // "gallon"
 */
export function getVolumeName(variant: VolumeUnit, count: number = 1): string {
  const unit = VOLUME_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown volume variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
